import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Buildings,
  CaretUpDown,
  Check,
  Envelope,
  Plus,
  User,
  Warning,
} from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { CompanyContactPerson } from 'shared/types/contact';
import { useCompanyContacts } from 'shared/hooks/useCompanyContacts';

export interface CompanyContactPickerProps {
  /** De firma (leverancier) waarvan de contactpersonen gekozen kunnen worden. */
  companyId: number | null | undefined;
  /** Naam van de firma; enkel voor de weergave ("Firma zelf — Elektro MS"). */
  companyName?: string | null;
  /**
   * Het algemene e-mailadres van de firma. Wordt getoond als de bestemming
   * wanneer er géén contactpersoon gekozen is, zodat altijd zichtbaar is waar de
   * mail naartoe gaat.
   */
  companyEmail?: string | null;
  /** De momenteel gekozen contactpersoon, of `null` voor "naar de firma zelf". */
  value: CompanyContactPerson | null | undefined;
  /**
   * Aangeroepen bij een keuze; `null` betekent "geen contactpersoon, mail naar de
   * firma". Mag een promise teruggeven — zolang die loopt is de kiezer bezig.
   */
  onChange: (contact: CompanyContactPerson | null) => void | Promise<void>;
  disabled?: boolean;
  /** Verbergt de knop om ter plekke een contactpersoon toe te voegen. */
  allowCreate?: boolean;
  /** Compacte variant voor gebruik in een chip of tabelcel. */
  compact?: boolean;
  className?: string;
}

interface CreateFormState {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  phone: string;
}

const LEEG_FORMULIER: CreateFormState = {
  firstName: '',
  lastName: '',
  jobTitle: '',
  email: '',
  phone: '',
};

/**
 * Kiezer voor de contactpersoon bij een firma, met de mogelijkheid om er ter
 * plekke een aan te maken.
 *
 * Waarom dit bestaat: bij een leverancier op een ticket of document wil je naar
 * een persoon kunnen mailen ("Jan van Elektro MS") i.p.v. naar het algemene
 * `info@`-adres van de firma. Staat die persoon nog niet in de contactendatabank,
 * dan moet je hem hier kunnen toevoegen zonder het ticket te verlaten.
 *
 * Bewust niet gekoppeld aan tickets of documenten: het component kent enkel een
 * firma en een gekozen contactpersoon. Alle drie de gebruikers (helpdeskticket,
 * renovatieticket, renovatiedocument) geven hun eigen `onChange` mee die de keuze
 * naar hun eigen endpoint bewaart.
 */
export const CompanyContactPicker: React.FC<CompanyContactPickerProps> = ({
  companyId,
  companyName,
  companyEmail,
  value,
  onChange,
  disabled = false,
  allowCreate = true,
  compact = false,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'list' | 'create'>('list');
  const [search, setSearch] = useState('');
  const [createForm, setCreateForm] = useState<CreateFormState>(LEEG_FORMULIER);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Contactpersonen pas ophalen wanneer de kiezer opengaat: op een ticket met
  // meerdere leveranciers zou anders elke kiezer meteen een query afvuren.
  const { contacts, isLoading, error, createContact } = useCompanyContacts(
    companyId,
    open,
  );

  // Bij het sluiten terug naar de lijst, zodat een half ingevuld formulier niet
  // blijft staan wanneer je later opnieuw opent.
  useEffect(() => {
    if (!open) {
      setMode('list');
      setSearch('');
      setCreateForm(LEEG_FORMULIER);
    }
  }, [open]);

  const fallbackLabel = useMemo(() => {
    if (companyEmail?.trim()) return companyEmail.trim();
    return companyName?.trim() || 'de firma';
  }, [companyEmail, companyName]);

  const handleSelect = useCallback(
    async (contact: CompanyContactPerson | null) => {
      setOpen(false);
      setIsSaving(true);
      try {
        await onChange(contact);
      } finally {
        setIsSaving(false);
      }
    },
    [onChange],
  );

  const handleCreate = useCallback(async () => {
    if (!createForm.firstName.trim() && !createForm.lastName.trim()) {
      toast.error('Vul minstens een voornaam of achternaam in');
      return;
    }

    setIsCreating(true);
    try {
      const created = await createContact({
        // Staat er enkel een achternaam, dan gebruiken we die als voornaam-veld
        // niet leeg mag zijn voor de weergave; de backend aanvaardt beide.
        firstName: createForm.firstName.trim() || createForm.lastName.trim(),
        lastName: createForm.firstName.trim() ? createForm.lastName.trim() : '',
        jobTitle: createForm.jobTitle,
        email: createForm.email,
        phone: createForm.phone,
      });
      toast.success(`${created.name} is toegevoegd aan ${companyName ?? 'de firma'}`);
      await handleSelect(created);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Contactpersoon kon niet aangemaakt worden',
      );
    } finally {
      setIsCreating(false);
    }
  }, [createForm, createContact, companyName, handleSelect]);

  /** Naar het aanmaakformulier, met de getypte zoekterm al als naam ingevuld. */
  const openCreateForm = useCallback(() => {
    const delen = search.trim().split(/\s+/).filter(Boolean);
    setCreateForm({
      ...LEEG_FORMULIER,
      firstName: delen[0] ?? '',
      lastName: delen.slice(1).join(' '),
    });
    setMode('create');
  }, [search]);

  const triggerLabel = value
    ? value.name
    : companyEmail?.trim()
      ? `Firma — ${companyEmail.trim()}`
      : 'Geen contactpersoon';

  const heeftGeenBestemming = !value?.hasEmail && !companyEmail?.trim();

  if (!companyId) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={compact ? 'sm' : 'default'}
          disabled={disabled || isSaving}
          className={className}
          title={
            value
              ? `Mail gaat naar ${value.email || 'geen adres bekend'}`
              : `Mail gaat naar ${fallbackLabel}`
          }
        >
          {isSaving ? (
            <Spinner />
          ) : value ? (
            <User />
          ) : heeftGeenBestemming ? (
            <Warning className="text-brick-primary" />
          ) : (
            <Buildings />
          )}
          <span className="truncate">{triggerLabel}</span>
          <CaretUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0" align="start">
        {mode === 'list' ? (
          <Command>
            <CommandInput
              placeholder="Zoek contactpersoon..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isLoading && (
                <div className="text-neutral-secondary-mid flex items-center justify-center gap-2 py-6 text-sm">
                  <Spinner />
                  Contactpersonen laden...
                </div>
              )}

              {!isLoading && error && (
                <div className="text-brick-primary px-3 py-6 text-center text-sm">
                  {error}
                </div>
              )}

              {!isLoading && !error && (
                <>
                  <CommandEmpty>Geen contactpersoon gevonden.</CommandEmpty>

                  {/* Terug naar het algemene adres van de firma. */}
                  <CommandGroup heading="Naar de firma zelf">
                    <CommandItem
                      value={`firma ${companyName ?? ''} ${companyEmail ?? ''}`}
                      onSelect={() => void handleSelect(null)}
                    >
                      <Buildings />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">
                          {companyName?.trim() || 'Firma'}
                        </span>
                        <span className="text-neutral-secondary-mid truncate text-xs">
                          {companyEmail?.trim() || 'Geen e-mailadres bij de firma'}
                        </span>
                      </div>
                      {!value && <Check className="ml-auto" />}
                    </CommandItem>
                  </CommandGroup>

                  {contacts.length > 0 && (
                    <CommandGroup heading="Contactpersonen">
                      {contacts.map((contact) => (
                        <CommandItem
                          key={contact.id}
                          value={`${contact.name} ${contact.email} ${contact.jobTitle ?? ''}`}
                          onSelect={() => void handleSelect(contact)}
                        >
                          <User />
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate">
                              {contact.name}
                              {contact.jobTitle && (
                                <span className="text-neutral-secondary-mid">
                                  {' '}
                                  — {contact.jobTitle}
                                </span>
                              )}
                            </span>
                            <span className="text-neutral-secondary-mid truncate text-xs">
                              {contact.hasEmail ? (
                                contact.email
                              ) : (
                                <span className="text-brick-primary">
                                  Geen e-mailadres
                                </span>
                              )}
                            </span>
                          </div>
                          {value?.id === contact.id && <Check className="ml-auto" />}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {allowCreate && (
                    <CommandGroup>
                      <CommandItem value="nieuwe-contactpersoon" onSelect={openCreateForm}>
                        <Plus className="text-moss-primary" />
                        <span className="text-moss-primary">
                          Contactpersoon toevoegen
                          {search.trim() && `: "${search.trim()}"`}
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        ) : (
          <div className="flex flex-col gap-3 p-3">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMode('list')}
                title="Terug naar de lijst"
              >
                <ArrowLeft />
              </Button>
              <span className="text-neutral-primary text-sm font-medium">
                Nieuwe contactpersoon
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="contact-voornaam">Voornaam</Label>
                <Input
                  id="contact-voornaam"
                  value={createForm.firstName}
                  placeholder="Jan"
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="contact-achternaam">Achternaam</Label>
                <Input
                  id="contact-achternaam"
                  value={createForm.lastName}
                  placeholder="Peeters"
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contact-functie">Functie</Label>
              <Input
                id="contact-functie"
                value={createForm.jobTitle}
                placeholder="Projectleider"
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, jobTitle: e.target.value }))
                }
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contact-email">E-mailadres</Label>
              <Input
                id="contact-email"
                type="email"
                value={createForm.email}
                placeholder="jan@elektro-ms.be"
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
              <span className="text-neutral-secondary-mid text-xs">
                Zonder adres kan er niet naar deze persoon gemaild worden.
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contact-telefoon">Telefoon</Label>
              <Input
                id="contact-telefoon"
                value={createForm.phone}
                placeholder="+32 ..."
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="text-neutral-secondary-mid flex items-center gap-1 text-xs">
                <Envelope />
                Wordt gekoppeld aan {companyName?.trim() || 'de firma'}
              </span>
              <Button onClick={() => void handleCreate()} disabled={isCreating}>
                {isCreating && <Spinner />}
                Toevoegen
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
